import { Vector4 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';

/**
 * Class containing static functions to help procedurally build meshes
 */
export class DiscBuilder {
    /**
     * Creates a torus mesh
     * * The parameter `diameter` sets the diameter size (float) of the torus (default 1)
     * * The parameter `thickness` sets the diameter size of the tube of the torus (float, default 0.5)
     * * The parameter `tessellation` sets the number of torus sides (postive integer, default 16)
     * * You can also set the mesh side orientation with the values : BABYLON.Mesh.FRONTSIDE (default), BABYLON.Mesh.BACKSIDE or BABYLON.Mesh.DOUBLESIDE
     * * If you create a double-sided mesh, you can choose what parts of the texture image to crop and stick respectively on the front and the back sides with the parameters `frontUVs` and `backUVs` (Vector4). Detail here : https://doc.babylonjs.com/babylon101/discover_basic_elements#side-orientation
     * * The mesh can be set to updatable with the boolean parameter `updatable` (default false) if its internal geometry is supposed to change once created.
     * @param name defines the name of the mesh
     * @param options defines the options used to create the mesh
     * @param scene defines the hosting scene
     * @returns the torus mesh
     * @see https://doc.babylonjs.com/how_to/set_shapes#torus
     */
    public static CreateDisc(
        name: string,
        options: {
            radius?: number;
            tessellation?: number;
            arc?: number;
            sideOrientation?: number;
            frontUVs?: Vector4;
            backUVs?: Vector4;
            updatable?: boolean;
        },
        scene: any
    ): Mesh {
        const disc = new Mesh(name, scene);

        options.sideOrientation = Mesh._GetDefaultSideOrientation(options.sideOrientation);
        disc._originalBuilderSideOrientation = options.sideOrientation;

        const vertexData = createDisc(options);

        vertexData.applyToMesh(disc, options.updatable);

        return disc;

        function createDisc(options: {
            radius?: number;
            tessellation?: number;
            arc?: number;
            sideOrientation?: number;
            frontUVs?: Vector4;
            backUVs?: Vector4;
        }) {
            const positions = new Array<number>();
            const indices = new Array<number>();
            const normals = new Array<number>();
            const uvs = new Array<number>();

            const radius = options.radius || 0.5;
            const tessellation = options.tessellation || 64;
            const arc: number = options.arc && (options.arc <= 0 || options.arc > 1) ? 1.0 : options.arc || 1.0;
            const sideOrientation =
                options.sideOrientation === 0 ? 0 : options.sideOrientation || VertexData.DEFAULTSIDE;

            // positions and uvs
            positions.push(0, 0, 0); // disc center first
            uvs.push(0.5, 0.5);

            const theta = Math.PI * 2 * arc;
            const step = theta / tessellation;
            for (let a = 0; arc === 1 ? a < theta : a <= theta; a += step) {
                const x = Math.cos(a);
                const y = Math.sin(a);
                const u = (x + 1) / 2;
                const v = (1 - y) / 2;
                positions.push(radius * x, radius * y, 0);
                uvs.push(u, v);
            }

            //indices
            const vertexNb = positions.length / 3;
            for (let i = 1; i < vertexNb - 1; i++) {
                indices.push(i + 1, 0, i);
            }

            if (arc === 1) {
                indices.push(1, 0, vertexNb - 1); // close the circle
            }

            // result
            VertexData.ComputeNormals(positions, indices, normals);
            VertexData._ComputeSides(
                sideOrientation,
                positions,
                indices,
                normals,
                uvs,
                options.frontUVs,
                options.backUVs
            );

            const vertexData = new VertexData();

            vertexData.indices = indices;
            vertexData.positions = positions;
            vertexData.normals = normals;
            vertexData.uvs = uvs;

            return vertexData;
        }
    }
}
